// Die horizontale Ansichts-Tableiste mit Unterstrich (Referenz:
// docs/design/01-roadmap-expanded.png).
//
// WOFUER: die Referenz-Optik verlangt eine Kopfzeile mit grossem Seitentitel und
// darunter Ansichts-Tabs mit Unterstrich. Die gab es nicht. Ersetzt zugleich die
// Emoji-Icons des bestehenden `ViewPicker` (Dropdown) durch lucide-Icons — der
// Emoji-Bruch endet hier.
//
// Tastaturbedienung nach dem vollstaendigen WAI-ARIA-Tabs-Muster mit
// automatischer Aktivierung: Pfeil links/rechts wandert (mit Umlauf), Home/End
// springt an den Rand, der Fokus folgt der Auswahl. Vorlage fuer das Niveau der
// Tastaturbedienung: components/ViewPicker.tsx.

import { useRef } from "preact/hooks";
import type { JSX } from "preact";
import type { LucideIcon } from "lucide-preact";

export interface ViewTabItem {
  id: string;
  label: string;
  /** lucide-Icon, keine Emojis. */
  icon?: LucideIcon;
  /** Zaehler rechts am Tab (z.B. Anzahl offener Aufgaben). */
  badge?: number | string;
  /** id des zugehoerigen Panels, falls die Seite eines rendert. */
  panelId?: string;
}

export interface ViewTabsProps {
  items: ViewTabItem[];
  active: string;
  onChange: (id: string) => void;
  /** Beschriftung der Leiste fuer Hilfstechnik. */
  label?: string;
  class?: string;
}

export function ViewTabs(props: ViewTabsProps): JSX.Element {
  const { items, active, onChange, label = "Ansichten", class: klass } = props;
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Auswahl setzen UND den Fokus mitnehmen (Fokus folgt der Auswahl).
  const move = (index: number): void => {
    const next = items[index];
    if (!next) return;
    onChange(next.id);
    btnRefs.current[next.id]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (items.length === 0) return;
    const current = items.findIndex((t) => t.id === active);
    const from = current >= 0 ? current : 0;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      move((from + 1) % items.length);
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      move((from - 1 + items.length) % items.length);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      move(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      move(items.length - 1);
      return;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      class={`jp-tabs-fade flex items-stretch gap-1 overflow-x-auto juuplan-no-scrollbar${
        klass ? ` ${klass}` : ""
      }`}
    >
      {items.map((t) => {
        const isActive = t.id === active;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            ref={(el) => {
              btnRefs.current[t.id] = el;
            }}
            type="button"
            role="tab"
            id={`viewtab-${t.id}`}
            aria-selected={isActive}
            aria-controls={t.panelId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(t.id)}
            class={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] ${
              isActive
                ? "border-[color:var(--color-primary)] text-[color:var(--color-primary)]"
                : "border-transparent text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
            }`}
          >
            {Icon && <Icon size={16} aria-hidden="true" />}
            <span>{t.label}</span>
            {t.badge !== undefined && t.badge !== "" && (
              <span
                class={`ml-0.5 rounded-full px-1.5 py-0.5 text-label font-semibold leading-none ${
                  isActive
                    ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
                    : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
                }`}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
